import { useEffect, useState } from 'react';
import { v1Admins, type V1Admin, sortAdminsByFirstName } from '../../../lib/v1';
import i18n from '../../../i18n';
import { formatDate, formatDateTime } from '../../../lib/dateFormat';

/* ─── Helpers ──────────────────────────────────────────────────────────────── */

// Einheitlich deutsch („26.10.2026 14:30"), unabhängig von UI-Sprache.
// Ungültige Werte werden roh angezeigt (wie bisher).
export function fmtDt(v: unknown): string {
  if (v == null || v === '') return '—';
  return formatDateTime(v as string | Date, String(v));
}

export function fmtDate(v: unknown): string {
  if (v == null || v === '') return '—';
  return formatDate(v as string | Date, String(v));
}

/**
 * Wandelt einen Server-Timestamp (ISO / "YYYY-MM-DD HH:mm:ss") in den von
 * <input type="datetime-local"> erwarteten lokalen String "YYYY-MM-DDTHH:mm".
 * Leerwert → "". Reines Datum bekommt 00:00. (T1)
 */
export function toLocalInput(v: unknown): string {
  if (v == null || v === '') return '';
  const s = String(v).trim();
  // reines Datum ohne Zeit → 00:00 ergänzen
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return `${s}T00:00`;
  const d = new Date(s.replace(' ', 'T'));
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/* ─── Aktueller Admin + Admin-Liste (Item 5: Ersteller/Bearbeiter) ────────── */

/**
 * Liest die ID des aktuell eingeloggten Admins aus dem von AuthContext
 * geschriebenen localStorage-Blob `sh24.user` (siehe api.ts meScope()).
 * Fallback null, wenn nicht vorhanden/parsebar.
 */
export function currentAdminId(): number | null {
  try {
    const raw = localStorage.getItem('sh24.user');
    if (!raw) return null;
    const u = JSON.parse(raw) as { id?: number };
    return typeof u.id === 'number' ? u.id : null;
  } catch {
    return null;
  }
}

export function adminLabel(a: V1Admin, tr: (key: string, opts?: Record<string, unknown>) => string = (k, opts) => i18n.t(k, opts)): string {
  const name = [a.vorname, a.nachname].filter(Boolean).join(' ').trim();
  return name || a.email || tr('pflege.dashboard.admin_fallback', { id: a.id });
}

/**
 * Lädt einmalig die Admin-Liste (GET /v1/admin/all-admins) für die
 * Bearbeiter-Auswahl.
 */
export function useActiveAdmins(): V1Admin[] {
  const [admins, setAdmins] = useState<V1Admin[]>([]);
  useEffect(() => {
    let alive = true;
    v1Admins.list()
      .then(list => {
        if (!alive) return;
        const sorted = sortAdminsByFirstName(list);
        setAdmins(sorted);
      })
      .catch(() => {/* Bearbeiter-Auswahl degradiert zu leer */});
    return () => { alive = false; };
  }, []);
  return admins;
}

/** Bearbeiter-Namen aus einer ID-Liste + Admin-Liste zusammensetzen. */
export function assigneeNames(ids: number[] | null | undefined, admins: V1Admin[]): string {
  if (!ids || ids.length === 0) return '';
  const byId = new Map(admins.map(a => [a.id, adminLabel(a)]));
  return ids.map(id => byId.get(id) ?? `#${id}`).join(', ');
}
